import { useTheme } from '@mui/material';
import { ParticipationBadge } from '@provision/chat-utils';
import {
  ConversationParticipantEntity,
  ConversationParticipantOption,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationParticipant } from '@provision/entity-types';
import { useMemo } from 'react';
import {
  getPreparedConversationParticipantOptionGroup,
  PreparedConversationParticipantOption,
} from 'src/graphql';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';

export type ParticipationBadgeMap = Record<string, ParticipationBadge>;

export const PARTICIPANT_CUSTOM_ICONS: Partial<Record<ConversationParticipantEntity, string>> =
  {
    [ConversationParticipantEntity.Bot]: 'smart_toy',
    [ConversationParticipantEntity.Rsm]: 'radar',
  };

export const getParticipantCustomIcon = (
  entity?: ConversationParticipantEntity,
): string | undefined => (entity ? PARTICIPANT_CUSTOM_ICONS[entity] : undefined);

type ParticipantIdentifiers = Pick<
  ParticipantSelectorItem | ConversationParticipant,
  'participantEntity' | 'participantIdentity'
>;

export const useParticipationBadgeMap = (): ParticipationBadgeMap => {
  const theme = useTheme();

  return useMemo(
    () => ({
      [ConversationParticipationMode.Assist]: {
        icon: 'support_agent',
        backgroundColor: theme.palette.custom.OV.sys.outlineVariant,
        iconColor: theme.palette.custom.OV.sys.secondary,
      },
    }),
    [theme],
  );
};

export function isSameParticipant(
  first: ParticipantIdentifiers,
  second: ParticipantIdentifiers,
): boolean {
  return (
    first.participantEntity === second.participantEntity &&
    first.participantIdentity === second.participantIdentity
  );
}

export function isSameParticipantOption(
  participant: ParticipantIdentifiers,
  option: PreparedConversationParticipantOption,
): boolean {
  return (
    participant.participantEntity ===
      <ParticipantIdentifiers['participantEntity']>option.participantEntity &&
    participant.participantIdentity === option.id
  );
}

export function getPreparedParticipantOption(
  option: ConversationParticipantOption,
): PreparedConversationParticipantOption {
  return {
    id: option.id,
    label: [option.firstName, option.lastName].join(' '),
    email: option.email,
    group: getPreparedConversationParticipantOptionGroup(option.participantEntity),
    participantEntity: option.participantEntity,
    conversationParticipantId: option.conversationParticipantId,
    phoneNumber: option.phoneNumber,
    value: option.id,
    entityPartnerId: option.entityPartnerId,
    avatarFileKey: option.avatarFileKey,
  };
}
